import * as React from "react";

interface NavigationSidebarContext {
  /** Whether the app navigation sidebar is open. */
  isSidebarOpen: boolean;

  /** Whether the mobile drawer is open. */
  isDrawerOpen: boolean;

  /** Close the app navigation sidebar. */
  close: () => void;

  /** Open the app navigation sidebar. */
  open: () => void;
}

/**
 * Context for the app left sidebar (navigation).
 *
 * Use `useNavigationSidebar` to access the context.
 */
export const NavigationSidebarContext =
  React.createContext<NavigationSidebarContext | null>(null);

/**
 * Custom hook to access the app's navigation sidebar context.
 *
 * Usage:
 * ```typescript
 * const sidebar = useNavigationSidebar();
 *
 * sidebar.isOpen;
 * sidebar.close();
 * sidebar.open();
 * ```
 */
export function useNavigationSidebar(): NavigationSidebarContext {
  const context = React.useContext(NavigationSidebarContext);

  if (!context) {
    throw new Error(
      "useNavigationSidebar must be used within NavigationSidebarProvider",
    );
  }

  return context;
}
